/**
 * StoryEditor Component
 * 
 * Full-featured editor for user stories with validation.
 * Displays as a modal or inline editor.
 */

import React, { useState, useEffect } from 'react';
import { 
  UserStory, 
  StandardTask,
  StoryValidation, 
  FieldError,
  STORY_POINTS_OPTIONS,
  PRIORITY_OPTIONS,
  shouldHaveStandardTasks,
  normalizePrioritySequence,
  formatPriorityTag
} from '../types/story';

interface StoryEditorProps {
  story: UserStory;
  onSave: (story: UserStory) => void;
  onCancel: () => void;
  isNew?: boolean;
}

/**
 * Validate a user story
 */
const validateStory = (story: UserStory): StoryValidation => {
  const errors: FieldError[] = [];
  const requiresStandardTasks = shouldHaveStandardTasks(`${story.rol} ${story.title}`);

  // Title validation
  if (!story.title || story.title.trim().length < 5) {
    errors.push({ field: 'title', message: 'El título debe tener al menos 5 caracteres' });
  }

  // User story format validation
  if (!story.user_story || story.user_story.trim().length < 20) {
    errors.push({ field: 'user_story', message: 'La descripción debe tener al menos 20 caracteres' });
  } else {
    // Check for "Como/Quiero/Para" format
    const hasFormat = /como\s+.+quiero\s+.+para\s+/i.test(story.user_story);
    if (!hasFormat) {
      errors.push({ 
        field: 'user_story', 
        message: 'Debe seguir el formato: Como [rol], quiero [acción], para [beneficio]' 
      });
    }
  }

  // Role validation
  if (!story.rol || story.rol.trim().length < 2) {
    errors.push({ field: 'rol', message: 'El rol es requerido' });
  }

  // Acceptance criteria validation
  if (story.criterios_funcionales.length === 0) {
    errors.push({ 
      field: 'criterios_funcionales', 
      message: 'Debe tener al menos un criterio de aceptación' 
    });
  }

  if (requiresStandardTasks && story.criterios_tecnicos.length === 0) {
    errors.push({
      field: 'criterios_tecnicos',
      message: 'Para [Front], [Back] e [Integración] se requieren criterios técnicos'
    });
  }

  // Story points validation
  if (!STORY_POINTS_OPTIONS.includes(story.story_points)) {
    errors.push({ 
      field: 'story_points', 
      message: 'Los story points deben ser de la secuencia Fibonacci (1, 2, 3, 5, 8, 13)' 
    });
  }

  if (story.priority_sequence !== undefined && story.priority_sequence !== null) {
    const normalizedSequence = normalizePrioritySequence(story.priority_sequence);
    if (!normalizedSequence) {
      errors.push({
        field: 'priority_sequence',
        message: 'La secuencia de prioridad debe ser un número entero mayor a 0'
      });
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

export const StoryEditor: React.FC<StoryEditorProps> = ({
  story: initialStory,
  onSave,
  onCancel,
  isNew = false
}) => {
  const [story, setStory] = useState<UserStory>(initialStory);
  const [validation, setValidation] = useState<StoryValidation>({ isValid: true, errors: [] });
  const [newCriterioFuncional, setNewCriterioFuncional] = useState('');
  const [newCriterioTecnico, setNewCriterioTecnico] = useState('');
  const [newFigmaLink, setNewFigmaLink] = useState('');
  const [newInsumo, setNewInsumo] = useState('');
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskDescription, setNewTaskDescription] = useState('');
  const [newTaskType, setNewTaskType] = useState<StandardTask['type']>('Desarrollo');
  const [newTaskHours, setNewTaskHours] = useState(1);
  const [showValidation, setShowValidation] = useState(false);

  // Sync state when opening another story
  useEffect(() => {
    setStory(initialStory);
    setValidation({ isValid: true, errors: [] });
    setShowValidation(false);
    setNewCriterioFuncional('');
    setNewCriterioTecnico('');
    setNewFigmaLink('');
    setNewInsumo('');
    setNewTaskTitle('');
    setNewTaskDescription('');
    setNewTaskType('Desarrollo');
    setNewTaskHours(1);
  }, [initialStory]);

  const handleFieldChange = (field: keyof UserStory, value: any) => {
    setStory(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddCriterio = () => {
    if (newCriterioFuncional.trim()) {
      setStory(prev => ({
        ...prev,
        criterios_funcionales: [...prev.criterios_funcionales, newCriterioFuncional.trim()]
      }));
      setNewCriterioFuncional('');
    }
  };

  const handleRemoveCriterio = (index: number) => {
    setStory(prev => ({
      ...prev,
      criterios_funcionales: prev.criterios_funcionales.filter((_, i) => i !== index)
    }));
  };

  const handleAddCriterioTecnico = () => {
    if (newCriterioTecnico.trim()) {
      setStory(prev => ({
        ...prev,
        criterios_tecnicos: [...prev.criterios_tecnicos, newCriterioTecnico.trim()]
      }));
      setNewCriterioTecnico('');
    }
  };

  const handleRemoveCriterioTecnico = (index: number) => {
    setStory(prev => ({
      ...prev,
      criterios_tecnicos: prev.criterios_tecnicos.filter((_, i) => i !== index)
    }));
  };

  const handleAddFigmaLink = () => {
    if (newFigmaLink.trim()) {
      setStory(prev => ({
        ...prev,
        figma_links: [...prev.figma_links, newFigmaLink.trim()]
      }));
      setNewFigmaLink('');
    }
  };

  const handleRemoveFigmaLink = (index: number) => {
    setStory(prev => ({
      ...prev,
      figma_links: prev.figma_links.filter((_, i) => i !== index)
    }));
  };

  const handleAddInsumo = () => {
    if (newInsumo.trim()) {
      setStory(prev => ({
        ...prev,
        insumos: [...prev.insumos, newInsumo.trim()]
      }));
      setNewInsumo('');
    }
  };

  const handleRemoveInsumo = (index: number) => {
    setStory(prev => ({
      ...prev,
      insumos: prev.insumos.filter((_, i) => i !== index)
    }));
  };

  const handleTaskHoursChange = (index: number, hours: number) => {
    setStory(prev => ({
      ...prev,
      tasks: prev.tasks.map((task, i) => 
        i === index ? { ...task, estimated_hours: hours } : task
      )
    }));
  };

  const handleTaskTypeChange = (index: number, type: StandardTask['type']) => {
    setStory(prev => ({
      ...prev,
      tasks: prev.tasks.map((task, i) =>
        i === index ? { ...task, type } : task
      )
    }));
  };

  const handleTaskTitleChange = (index: number, title: string) => {
    setStory(prev => ({
      ...prev,
      tasks: prev.tasks.map((task, i) =>
        i === index ? { ...task, title } : task
      )
    }));
  };

  const handleTaskDescriptionChange = (index: number, description: string) => {
    setStory(prev => ({
      ...prev,
      tasks: prev.tasks.map((task, i) =>
        i === index ? { ...task, description } : task
      )
    }));
  };

  const handleRemoveTask = (index: number) => {
    setStory(prev => ({
      ...prev,
      tasks: prev.tasks.filter((_, i) => i !== index)
    }));
  };

  const handleAddTask = () => {
    const title = newTaskTitle.trim();
    if (!title) return;

    setStory(prev => ({
      ...prev,
      tasks: [
        ...prev.tasks,
        {
          title,
          description: newTaskDescription.trim(),
          type: newTaskType,
          estimated_hours: Math.max(1, newTaskHours)
        }
      ]
    }));

    setNewTaskTitle('');
    setNewTaskDescription('');
    setNewTaskType('Desarrollo');
    setNewTaskHours(1);
  };

  const handleSave = () => {
    const result = validateStory(story);
    setValidation(result);
    setShowValidation(true);

    if (result.isValid) {
      onSave(story);
    }
  };

  const getFieldError = (field: keyof UserStory): string | undefined => {
    if (!showValidation) return undefined;
    return validation.errors.find(e => e.field === field)?.message;
  };

  const requiresStandardTasks = shouldHaveStandardTasks(`${story.rol} ${story.title}`);
  const priorityTag = formatPriorityTag(story.priority_sequence);
  const priorityDisplay = priorityTag
    ? (story.priority === 'Media' ? priorityTag : `${story.priority} ${priorityTag}`)
    : story.priority;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
          <h2 className="text-xl font-bold">
            {isNew ? '➕ Nueva Historia de Usuario' : '✏️ Editar Historia de Usuario'}
          </h2>
          <p className="text-sm text-blue-100 mt-1">
            Complete todos los campos requeridos para la historia
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Validation Errors Summary */}
          {showValidation && !validation.isValid && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h4 className="font-semibold text-red-800 mb-2">⚠️ Errores de validación</h4>
              <ul className="text-sm text-red-700 space-y-1">
                {validation.errors.map((error, idx) => (
                  <li key={idx}>• {error.message}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Basic Info Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Title */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Título <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={story.title}
                onChange={(e) => handleFieldChange('title', e.target.value)}
                placeholder="Ej: Implementar autenticación con OAuth2"
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  getFieldError('title') ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {getFieldError('title') && (
                <p className="text-red-500 text-xs mt-1">{getFieldError('title')}</p>
              )}
            </div>

            {/* Role */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rol <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={story.rol}
                onChange={(e) => handleFieldChange('rol', e.target.value)}
                placeholder="Ej: Usuario, Administrador, Desarrollador"
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                  getFieldError('rol') ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {getFieldError('rol') && (
                <p className="text-red-500 text-xs mt-1">{getFieldError('rol')}</p>
              )}
            </div>

            {/* Epic */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Épica
              </label>
              <input
                type="text"
                value={story.epic || ''}
                onChange={(e) => handleFieldChange('epic', e.target.value)}
                placeholder="Ej: Autenticación y Seguridad"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Priority */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Prioridad <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-2">
                <select
                  value={story.priority}
                  onChange={(e) => handleFieldChange('priority', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  {PRIORITY_OPTIONS.map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={story.priority_sequence ?? ''}
                  onChange={(e) => {
                    const normalized = normalizePrioritySequence(e.target.value);
                    handleFieldChange('priority_sequence', normalized);
                  }}
                  placeholder="Secuencia (ej: 1)"
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                    getFieldError('priority_sequence') ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
              </div>
              {getFieldError('priority_sequence') && (
                <p className="text-red-500 text-xs mt-1">{getFieldError('priority_sequence')}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Puedes usar nivel (Alta/Media/Baja) y/o secuencia numérica.
              </p>
              {priorityDisplay && (
                <span className="inline-flex mt-2 px-2 py-1 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700 border border-indigo-200">
                  Prioridad aplicada: {priorityDisplay}
                </span>
              )}
            </div>

            {/* Story Points */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Story Points <span className="text-red-500">*</span>
              </label>
              <select
                value={story.story_points}
                onChange={(e) => handleFieldChange('story_points', parseInt(e.target.value))}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                  getFieldError('story_points') ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                {STORY_POINTS_OPTIONS.map(sp => (
                  <option key={sp} value={sp}>{sp} puntos</option>
                ))}
              </select>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Estado
              </label>
              <select
                value={story.status}
                onChange={(e) => handleFieldChange('status', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="draft">Borrador</option>
                <option value="review">En Revisión</option>
                <option value="approved">Aprobada</option>
              </select>
            </div>
          </div>

          {/* User Story Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Historia de Usuario <span className="text-red-500">*</span>
            </label>
            <textarea
              value={story.user_story}
              onChange={(e) => handleFieldChange('user_story', e.target.value)}
              placeholder="Como [rol], quiero [acción específica], para [beneficio/valor de negocio]"
              rows={3}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                getFieldError('user_story') ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {getFieldError('user_story') && (
              <p className="text-red-500 text-xs mt-1">{getFieldError('user_story')}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Formato requerido: Como [rol], quiero [acción], para [beneficio]
            </p>
          </div>

          {/* Acceptance Criteria */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Criterios Funcionales <span className="text-red-500">*</span>
              </label>
              <div className="space-y-2 mb-3">
                {story.criterios_funcionales.map((criterio, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-gray-50 p-2 rounded">
                    <span className="text-green-500">✓</span>
                    <span className="flex-1 text-sm">{criterio}</span>
                    <button
                      onClick={() => handleRemoveCriterio(idx)}
                      className="text-red-500 hover:text-red-700 text-sm"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newCriterioFuncional}
                  onChange={(e) => setNewCriterioFuncional(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddCriterio()}
                  placeholder="DADO que... CUANDO... ENTONCES..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={handleAddCriterio}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  + Agregar
                </button>
              </div>
              {getFieldError('criterios_funcionales') && (
                <p className="text-red-500 text-xs mt-1">{getFieldError('criterios_funcionales')}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Criterios Técnicos
                {requiresStandardTasks && <span className="text-red-500 ml-1">*</span>}
                <span className="text-xs text-gray-500 ml-2">(Auto por stack [Front]/[Back]/[Integración])</span>
              </label>
              <div className="space-y-2 mb-3">
                {story.criterios_tecnicos.map((criterio, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-blue-50 p-2 rounded">
                    <span className="text-blue-500">⚙️</span>
                    <span className="flex-1 text-sm">{criterio}</span>
                    <button
                      onClick={() => handleRemoveCriterioTecnico(idx)}
                      className="text-red-500 hover:text-red-700 text-sm"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newCriterioTecnico}
                  onChange={(e) => setNewCriterioTecnico(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddCriterioTecnico()}
                  placeholder="Criterio técnico verificable..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={handleAddCriterioTecnico}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  + Agregar
                </button>
              </div>
              {getFieldError('criterios_tecnicos') && (
                <p className="text-red-500 text-xs mt-1">{getFieldError('criterios_tecnicos')}</p>
              )}
            </div>
          </div>

          {/* Standard Tasks */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tareas
              <span className="text-xs text-gray-500 ml-2">(Puedes agregar, editar o quitar tareas)</span>
            </label>
            {story.tasks.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {story.tasks.map((task, idx) => (
                  <div 
                    key={idx} 
                    className="p-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg border border-gray-200"
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <input
                        type="text"
                        value={task.title}
                        onChange={(e) => handleTaskTitleChange(idx, e.target.value)}
                        className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
                        placeholder="Título de la tarea"
                      />
                      <button
                        onClick={() => handleRemoveTask(idx)}
                        className="px-2 py-1 text-xs text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                        title="Eliminar tarea"
                      >
                        ✕
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <select
                        value={task.type}
                        onChange={(e) => handleTaskTypeChange(idx, e.target.value as StandardTask['type'])}
                        className="px-2 py-1 text-sm border border-gray-300 rounded"
                      >
                        <option value="Contextualización">Contextualización</option>
                        <option value="Desarrollo">Desarrollo</option>
                        <option value="Pruebas">Pruebas</option>
                        <option value="Despliegue">Despliegue</option>
                      </select>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="1"
                          max="40"
                          value={task.estimated_hours}
                          onChange={(e) => handleTaskHoursChange(idx, parseInt(e.target.value, 10) || 1)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        />
                        <span className="text-xs text-gray-500">h</span>
                      </div>
                    </div>
                    <textarea
                      value={task.description}
                      onChange={(e) => handleTaskDescriptionChange(idx, e.target.value)}
                      rows={2}
                      placeholder="Descripción de la tarea (opcional)"
                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded p-3">
                No hay tareas configuradas para esta HU.
              </div>
            )}

            <div className="mt-3 p-3 bg-white border border-gray-200 rounded-lg">
              <p className="text-xs font-semibold text-gray-700 mb-2">Agregar tarea</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <input
                  type="text"
                  value={newTaskTitle}
                  onChange={(e) => setNewTaskTitle(e.target.value)}
                  placeholder="Título de la tarea"
                  className="px-3 py-2 text-sm border border-gray-300 rounded"
                />
                <select
                  value={newTaskType}
                  onChange={(e) => setNewTaskType(e.target.value as StandardTask['type'])}
                  className="px-3 py-2 text-sm border border-gray-300 rounded"
                >
                  <option value="Contextualización">Contextualización</option>
                  <option value="Desarrollo">Desarrollo</option>
                  <option value="Pruebas">Pruebas</option>
                  <option value="Despliegue">Despliegue</option>
                </select>
                <textarea
                  value={newTaskDescription}
                  onChange={(e) => setNewTaskDescription(e.target.value)}
                  rows={2}
                  placeholder="Descripción (opcional)"
                  className="px-3 py-2 text-sm border border-gray-300 rounded md:col-span-2"
                />
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="1"
                    max="40"
                    value={newTaskHours}
                    onChange={(e) => setNewTaskHours(Math.max(1, parseInt(e.target.value, 10) || 1))}
                    className="w-24 px-3 py-2 text-sm border border-gray-300 rounded"
                  />
                  <span className="text-xs text-gray-500">horas estimadas</span>
                </div>
                <button
                  onClick={handleAddTask}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm font-medium"
                >
                  + Agregar tarea
                </button>
              </div>
            </div>

            {requiresStandardTasks && story.tasks.length === 0 && (
              <p className="text-amber-600 text-xs mt-2">
                Este frente suele manejar tareas estándar, pero puedes definirlas según la necesidad de la HU.
              </p>
            )}
          </div>

          {/* Figma Links */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Enlaces de Figma
            </label>
            <div className="space-y-2 mb-3">
              {story.figma_links.map((link, idx) => (
                <div key={idx} className="flex items-center gap-2 bg-pink-50 p-2 rounded">
                  <span>🎨</span>
                  <a 
                    href={link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex-1 text-sm text-blue-600 hover:underline truncate"
                  >
                    {link}
                  </a>
                  <button
                    onClick={() => handleRemoveFigmaLink(idx)}
                    className="text-red-500 hover:text-red-700 text-sm"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="url"
                value={newFigmaLink}
                onChange={(e) => setNewFigmaLink(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddFigmaLink()}
                placeholder="https://figma.com/file/..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={handleAddFigmaLink}
                className="px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors"
              >
                + Figma
              </button>
            </div>
          </div>

          {/* Insumos/Resources */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Insumos / Recursos
            </label>
            <div className="space-y-2 mb-3">
              {story.insumos.map((insumo, idx) => (
                <div key={idx} className="flex items-center gap-2 bg-gray-50 p-2 rounded">
                  <span>📎</span>
                  <span className="flex-1 text-sm">{insumo}</span>
                  <button
                    onClick={() => handleRemoveInsumo(idx)}
                    className="text-red-500 hover:text-red-700 text-sm"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newInsumo}
                onChange={(e) => setNewInsumo(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddInsumo()}
                placeholder="Documento, API, Servicio externo..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={handleAddInsumo}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                + Insumo
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            {isNew ? '✓ Crear Historia' : '✓ Guardar Cambios'}
          </button>
        </div>
      </div>
    </div>
  );
};
